import { createFileRoute, Navigate } from "@tanstack/react-router";

import { isCustomerParticipantRole } from "../../lib/access";
import { cachedProjectAt } from "../../lib/prefetch";
import { loadedSession } from "../../lib/session";
import { useProject, useProjectId } from "../../shell/project";
import { leadSearch } from "../../workspaces/agents/agent-location";
import { agentWorkspaceEnabled } from "../../workspaces/agents/availability";
import { usePageArtifactAddress } from "../../workspaces/agents/beside/address";
import { LeadConversation } from "../../workspaces/agents/LeadConversation";

export const Route = createFileRoute("/_console/p/$org/$project/")({
  // The home's reads start on click: the Library's first page and its map (the operation
  // beside the conversation), and the lead agent's conversation.
  loader: ({ context, params, cause }) => {
    if (cause === "stay") return;
    const address = { org: params.org, project: params.project };
    const project = cachedProjectAt(context.queryClient, loadedSession(context.queryClient)?.principal.id, address);
    if (project !== undefined) {
      // The catalog's client is the page's, not the first paint's: loaded here, not imported.
      void Promise.all([
        import("../../workspaces/library/api"),
        import("../../workspaces/library/queries"),
      ]).then(([{ createCatalogApi }, { libraryQueries }]) => {
        const library = libraryQueries(project.id, createCatalogApi(project.id));
        void context.queryClient.prefetchQuery(library.episodeTotals({ stock: undefined, task: undefined }));
        void context.queryClient.prefetchQuery(library.episodeMap);
      });
    }
    if (agentWorkspaceEnabled) {
      void import("../../workspaces/agents/prefetch").then(({ prefetchAgentPage }) =>
        prefetchAgentPage(context.queryClient, address, undefined, undefined),
      );
    }
  },
  validateSearch: leadSearch,
  component: ProjectHomeRoute,
});

/** A project opens on its operation: the lead agent's conversation, with the month it runs
 *  beside it. A customer reviewer opens on the results the project holds. */
function ProjectHomeRoute() {
  const params = Route.useParams();
  const { selected } = useProject();
  if (isCustomerParticipantRole(selected?.managedAccessRole))
    return <Navigate to="/p/$org/$project/library" params={params} replace />;
  return <ProjectHome />;
}

function ProjectHome() {
  const projectId = useProjectId();
  const { address } = useProject();
  const { chat, tab, revision, artifact, pane, pinned } = Route.useSearch();
  const navigate = Route.useNavigate();
  const shown = usePageArtifactAddress({
    artifact,
    pane,
    pinned,
    onAddress: (next, replace) => void navigate({ search: (previous) => ({ ...previous, ...next }), replace }),
  });
  if (!address) return null;
  return (
    <LeadConversation
      projectId={projectId}
      address={address}
      chat={chat}
      tab={tab}
      revision={revision}
      artifact={shown}
      onSelectSession={(sessionId, options) =>
        void navigate({
          search: (previous) => ({
            ...previous,
            chat: sessionId,
            ...(options?.replace ? {} : { tab: undefined, revision: undefined, artifact: undefined, pane: undefined }),
          }),
          replace: options?.replace,
        })
      }
      onTabChange={(next) => void navigate({ search: (previous) => ({ ...previous, tab: next, revision: undefined }) })}
    />
  );
}
